import type { Capability } from '@viq-admin/ui';
import type { Role } from '../auth/useRoleClaim';

/**
 * The portal's capability map: capability → roles permitted to exercise it.
 *
 * This is NOT derived from the AppShell nav allow-lists — those gate whole
 * routes, not actions. It mirrors the backend authMatrix, and
 * `__tests__/components/permissionMatrix.test.tsx` asserts the two agree so
 * they cannot drift silently.
 *
 * Client gating is UX-only; `adminAuth({ roles })` on /v1/admin/* is the gate
 * that matters (docs/feature-authoring.md).
 */
export const PORTAL_CAPABILITIES: Record<Capability, Role[]> = {
  'read:Conference': ['company_admin'],
  'manage:Conference': ['company_admin'],
  'read:User': ['company_admin'],
  'manage:User': ['company_admin'],
  'read:DiscoveryQuestion': ['company_admin'],
  'manage:DiscoveryQuestion': ['company_admin'],
  'read:Lead': ['company_admin', 'stakeholder'],
  // Every per-lead mutation on /v1/admin (tags, assignment, archive, restore,
  // field edits) is `adminAuth({ roles: ['company_admin'] })` — see the
  // backend's authMatrix. Gates the Batch screen's bulk actions.
  'manage:Lead': ['company_admin'],
  'manage:CrmIntegration': ['company_admin'],
  // PUT /v1/admin/company/lead-rules is `adminAuth({ roles: ['company_admin'] })`
  // (backend authMatrix). Gates the Lead rules editor's controls.
  'manage:LeadRules': ['company_admin'],
};

/**
 * Build the predicate injected into `<CapabilityProvider>`. An unregistered
 * capability, or a null role, is denied — matching the console predicate.
 *
 * Guarded with `Object.hasOwn` rather than a truthiness check on the looked-up
 * value: `PORTAL_CAPABILITIES` is a plain object literal, so
 * `PORTAL_CAPABILITIES['toString']` (or `'constructor'`, `'__proto__'`)
 * resolves to an inherited, truthy `Object.prototype` member and would
 * otherwise slip past a `!allowed` check — `allowed.includes(role)` then
 * throws instead of denying, breaking `CapabilityContextValue.can`'s "MUST
 * return false for any capability not in the registry" contract.
 */
export function buildPortalCan(
  role: Role | null,
): (cap: Capability) => boolean {
  return (cap: Capability) => {
    if (!role || !Object.hasOwn(PORTAL_CAPABILITIES, cap)) return false;
    return PORTAL_CAPABILITIES[cap].includes(role);
  };
}
